import { useCallback, useEffect, useState } from "react";
import { API_BASE } from '@/lib/sovereign';
import { tenantIdFromHost } from '@/lib/tenant-host';
import { useVisiblePoll } from './use-visible-poll';

interface LGNSPriceResult {
  price: number | null;
  change24h: number | null;
  loading: boolean;
  error: string | null;
  lastUpdated: Date | null;
  refresh: () => void;
}

/**
 * Matches the Cache-Control max-age the API now sends and sits inside its 120s
 * server-side cache, so polling faster would only re-read the same value.
 */
const REFRESH_INTERVAL_MS = 60_000;

/**
 * Floor on the spacing between two network calls, a little under the interval
 * so ordinary timer jitter does not skip a legitimate tick.
 *
 * This is what makes the price cost one request per minute no matter how many
 * components ask for it. The hook used to hold its own state and its own
 * interval per instance, and `landing.tsx` mounts it twice — so an idle
 * landing tab made two requests a minute, forever.
 */
const MIN_FETCH_SPACING_MS = 50_000;

type PriceSnapshot = Omit<LGNSPriceResult, 'refresh'>;

interface PriceResponse {
  price?: number;
  priceUSD?: number;
  change24h?: number | null;
}

// ─── Module-level store, shared by every mounted instance ─────────────────────

let snapshot: PriceSnapshot = {
  price: null,
  change24h: null,
  loading: true,
  error: null,
  lastUpdated: null,
};

const listeners = new Set<() => void>();
let inflight: Promise<void> | null = null;
let lastFetchStartedAt = 0;

function publish(next: Partial<PriceSnapshot>): void {
  snapshot = { ...snapshot, ...next };
  for (const listener of listeners) listener();
}

/**
 * Fetch the price into the shared store.
 *
 * Collapses to a no-op when a request is already in flight, or when one
 * completed too recently — except for `force`, which is what the user-facing
 * refresh button uses.
 */
export function fetchLgnsPrice({ force = false }: { force?: boolean } = {}): Promise<void> {
  if (inflight) return inflight;
  if (!force && Date.now() - lastFetchStartedAt < MIN_FETCH_SPACING_MS) {
    return Promise.resolve();
  }

  lastFetchStartedAt = Date.now();
  publish({ loading: true, error: null });

  inflight = (async () => {
    try {
      const res = await fetch(`${API_BASE}/api/v1/price`, {
        headers: { 'X-Tenant-ID': tenantIdFromHost() },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const data = (await res.json()) as PriceResponse;
      const price = data?.price ?? data?.priceUSD;
      if (typeof price === 'number' && price > 0) {
        publish({
          price,
          change24h: data.change24h ?? null,
          lastUpdated: new Date(),
          error: null,
          loading: false,
        });
      } else {
        publish({
          error: 'Price not available — all providers returned empty',
          loading: false,
        });
      }
    } catch (err) {
      publish({
        error: err instanceof Error ? err.message : 'Failed to fetch price',
        loading: false,
      });
    }
  })().finally(() => {
    inflight = null;
  });

  return inflight;
}

/** Reset the shared store. Tests only. */
export function resetLgnsPriceStore(): void {
  snapshot = { price: null, change24h: null, loading: true, error: null, lastUpdated: null };
  listeners.clear();
  inflight = null;
  lastFetchStartedAt = 0;
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useLGNSPrice(): LGNSPriceResult {
  const [, bumpRender] = useState(0);

  useEffect(() => {
    const listener = () => bumpRender((n) => n + 1);
    listeners.add(listener);
    // The first instance to mount starts the initial load; later instances
    // render whatever is already in the store.
    void fetchLgnsPrice();
    return () => {
      listeners.delete(listener);
    };
  }, []);

  // Public market data, so this polls whether or not anyone is signed in — but
  // only while the tab is actually on screen.
  useVisiblePoll(() => {
    void fetchLgnsPrice();
  }, REFRESH_INTERVAL_MS);

  const refresh = useCallback(() => {
    void fetchLgnsPrice({ force: true });
  }, []);

  return { ...snapshot, refresh };
}
